import { NavLink, useParams } from "react-router";
import { useAuth } from "react-oidc-context";
import { ProfilePicture } from "../../components/profile/ProfilePicture.tsx";
import {
  useAllTeacherFeedbackQuery,
  useTeacherQuery,
} from "../../api/teacherApi.ts";
import { Testimonials } from "./Testimonials.tsx";
import MediaWall from "../../components/media/MediaWall.tsx";
import { Trans } from "@lingui/react/macro";
import { useMemo } from "react";
import { useSupportedLanguages } from "../../api/languageApi.ts";
import { LucideIcon, Music } from "lucide-react";
import {
  getInstrumentIcon,
  getTranslation,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { useSupportedGenres } from "../../api/genreApi.ts";
import { useQueryUrl } from "../../hooks/useQueryNav.ts";

export const Profile = () => {
  const { user } = useAuth();
  const { userId } = useParams();
  const { data, error, isLoading } = useTeacherQuery(userId);
  const { data: teacherFeedback } = useAllTeacherFeedbackQuery(userId);
  const { data: supportedLanguages } = useSupportedLanguages();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { data: supportedGenres } = useSupportedGenres();
  const { language } = useLanguageStore();
  const buildUrl = useQueryUrl();

  const languages = useMemo(
    () =>
      supportedLanguages?.filter((language) =>
        data?.languages.includes(language.locale),
      ) ?? [],
    [supportedLanguages, data],
  );

  const instruments = useMemo(() => {
    return supportedInstruments.filter((instrument) =>
      data?.instruments.includes(instrument.name),
    );
  }, [supportedInstruments, data]);

  const genres = useMemo(() => {
    return (
      supportedGenres?.filter((genre) => data?.genres.includes(genre.name)) ??
      []
    );
  }, [supportedGenres, data]);

  if (isLoading)
    return (
      <div
        className="flex flex-col items-center justify-center py-20"
        role="status"
      >
        <div className="relative h-12 w-12">
          <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4" />
          <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent" />
        </div>
        <p className="text-soluud-text-secondary mt-4 text-sm font-medium">
          <Trans>Loading profile…</Trans>
        </p>
        <span className="sr-only">Loading</span>
      </div>
    );
  if (error)
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <p className="text-soluud-text-secondary text-base">
          <Trans>Could not load this profile. Please try again later.</Trans>
        </p>
      </div>
    );

  return (
    <div className="container mx-auto flex flex-col justify-center gap-6 p-4 lg:flex-row lg:items-start">
      {/* Left Section */}
      <section className="bg-soluud-background-card border-soluud-border-subtle relative rounded-2xl border p-6 shadow-[--shadow-card] lg:w-1/3">
        {/* Profile Image and Info */}
        <div className="flex flex-col items-center">
          <div className="ring-soluud-primary/10 relative mb-4 h-32 w-32 rounded-full ring-4">
            <ProfilePicture
              profilePictureUrl={data?.profilePictureUrl}
              firstName={data?.firstName}
              lastName={data?.lastName}
              className="h-full w-full"
              iconSize={72}
            />
          </div>
          <h2 className="text-xl font-semibold tracking-tight">
            {data?.firstName} {data?.lastName}
          </h2>
          {languages.length > 0 && (
            <div className="mt-2 inline-flex flex-wrap items-center justify-center gap-2">
              {languages.map((lang) => (
                <span
                  key={lang.locale}
                  className="bg-soluud-primary-subtle inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm"
                >
                  <span
                    className={`fi fi-${lang.country}`}
                    title={lang.displayText}
                  />
                  <span className="text-soluud-text-secondary text-xs">
                    {lang.displayText}
                  </span>
                </span>
              ))}
            </div>
          )}
          {data?.introduction && (
            <p className="text-soluud-text-primary my-3 max-w-full text-center text-sm leading-relaxed break-words whitespace-pre-line">
              {data?.introduction}
            </p>
          )}
          {instruments.length > 0 && (
            <div className="border-soluud-border-subtle flex w-full flex-wrap justify-center gap-x-1 border-t py-3">
              {instruments
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((instrument) => {
                  const IconComponent: LucideIcon = getInstrumentIcon(
                    instrument.name,
                  );
                  return (
                    <div
                      key={instrument.name}
                      className="bg-soluud-background-card flex w-16 min-w-fit flex-col items-center justify-center p-2 text-center"
                    >
                      <div className="mb-2">
                        <IconComponent size={20} />
                      </div>
                      <span className="text-soluud-text-secondary text-xs font-medium sm:text-sm">
                        {getTranslation(instrument, language)}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
          {genres.length > 0 && (
            <>
              <div className="border-soluud-border-subtle flex w-full flex-wrap justify-center gap-x-1 border-t py-3">
                {genres
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((genre) => {
                    return (
                      <div
                        key={genre.name}
                        className="bg-soluud-background-card flex w-16 min-w-fit flex-col items-center justify-center p-2 text-center"
                      >
                        <div className="mb-2">
                          <Music size={20} />
                        </div>
                        <span className="text-soluud-text-secondary text-xs font-medium sm:text-sm">
                          {getTranslation(genre, language)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </>
          )}

          <div className={"mb-4 w-full"}>
            <Testimonials
              testimonials={teacherFeedback?.teacherFeedback || []}
            />
          </div>

          {/* Buttons — clear visual hierarchy */}
          <div className="border-soluud-border-subtle mt-2 flex w-full flex-col gap-2.5 border-t pt-4">
            {data && data.teacherStatus == "COMPLETE" && (
              <>
                <NavLink
                  className="w-full"
                  to={buildUrl(`/teachers/${userId}/bookings/new`)}
                >
                  <button className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active w-full cursor-pointer rounded-xl py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]">
                    <Trans>Book Lesson Now</Trans>
                  </button>
                </NavLink>
                <NavLink
                  className="w-full"
                  to={buildUrl(`/teachers/${userId}/subscriptions/new`)}
                >
                  <button className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active w-full cursor-pointer rounded-xl py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]">
                    <Trans>Subscribe</Trans>
                  </button>
                </NavLink>
                <NavLink
                  className="w-full"
                  to={`/mystudio/messages?newChatUserId=${userId}`}
                >
                  <button className="border-soluud-primary text-soluud-primary hover:bg-soluud-primary-light w-full cursor-pointer rounded-xl border bg-transparent py-2.5 text-sm font-medium transition-all duration-150 active:scale-[0.98]">
                    <Trans>Contact</Trans>
                  </button>
                </NavLink>
              </>
            )}

            {user?.profile.sub === userId && (
              <NavLink className="w-full" to="/mystudio/profile">
                <button className="text-soluud-text-secondary hover:text-soluud-text-primary hover:bg-soluud-background w-full cursor-pointer rounded-xl py-2.5 text-sm font-medium transition-all duration-150 active:scale-[0.98]">
                  <Trans>Edit Profile</Trans>
                </button>
              </NavLink>
            )}
          </div>
        </div>
      </section>

      {/* Right Section - Media Wall */}
      <section className="bg-soluud-background-card border-soluud-border-subtle flex min-h-[400px] flex-1 flex-col rounded-2xl border p-6 shadow-[--shadow-card] lg:max-w-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight">
            <Trans>Media Wall</Trans>
          </h3>
          {user?.profile.sub === userId && (
            <NavLink to={`/profile/${userId}/media/upload`}>
              <button className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-150 active:scale-[0.98]">
                <Trans>Upload Media</Trans>
              </button>
            </NavLink>
          )}
        </div>
        <MediaWall userId={userId} />
      </section>
    </div>
  );
};
